import dayjs, { Dayjs } from 'dayjs'
import { styled } from '@mui/material/styles'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { useCompanySettings } from '$hooks'
import { ComponentType, FC, useState } from 'react'
import { isDateInList } from 'src/components/common/helpers'
import { Nullable } from '$models'

type WeekPickerType = 'day' | 'week' | 'month'

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  isSelected: boolean
  isHovered: boolean
  isHighlighted: boolean
  weekStartPreference?: number
  pickerType?: WeekPickerType
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== 'isSelected' &&
    prop !== 'isHovered' &&
    prop !== 'isHighlighted' &&
    prop !== 'weekStartPreference' &&
    prop !== 'pickerType',
})<CustomPickerDayProps>(
  ({
    theme,
    isSelected,
    isHovered,
    day,
    weekStartPreference = 0,
    isHighlighted = false,
    pickerType = 'day',
  }) => ({
    borderRadius: 0,
    ...(isHighlighted && {
      backgroundColor: theme.palette.primary.dark,
      color: `${theme.palette.primary.contrastText} !important`,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.dark,
      },
    }),

    ...(!isHighlighted &&
      isSelected && {
        backgroundColor: theme.palette.primary.light,
        color: `${theme.palette.primary.contrastText} !important`,
        '&:hover, &:focus': {
          backgroundColor: theme.palette.primary.light,
        },
      }),
    ...(!isHighlighted &&
      isHovered && {
        backgroundColor: theme.palette.primary.light,
        color: `${theme.palette.primary.contrastText} !important`,
        '&:hover, &:focus': {
          backgroundColor: theme.palette.primary.light,
        },
      }),
    ...(pickerType === 'week' && getWeekStyles(day, weekStartPreference)),
    ...(pickerType === 'month' && getMonthStyles(day)),
    ...(pickerType === 'day' && getDayStyles(day)),
  })
) as ComponentType<CustomPickerDayProps>

const getWeekStyles = (day: Dayjs, weekStartPreference: number) => ({
  ...(day.day() === weekStartPreference && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(day.day() === (weekStartPreference + 6) % 7 && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
})

const getMonthStyles = (day: Dayjs) => ({
  ...(day.isSame(day.startOf('month'), 'day') && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(day.isSame(day.endOf('month'), 'day') && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
})

const getDayStyles = (day: Dayjs) => ({
  ...(day.isSame(day.startOf('day'), 'day') && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(day.isSame(day.endOf('day'), 'day') && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
})

const isInSame = (
  dayA: Dayjs,
  dayB: Dayjs | null | undefined,
  type: WeekPickerType
) => {
  if (dayB == null) {
    return false
  }

  return dayA.isSame(dayB, type)
}

function Day(
  props: PickersDayProps<Dayjs> & {
    selectedDay?: Dayjs | null
    hoveredDay?: Dayjs | null
    weekStartPreference?: number
    highlightedDays?: Dayjs[]
    pickerType?: WeekPickerType
  }
) {
  const {
    day,
    selectedDay,
    hoveredDay,
    weekStartPreference,
    highlightedDays = [],
    pickerType = 'day',
    ...other
  } = props
  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ paddingX: 2.5 }}
      disableMargin
      selected={false}
      isSelected={isInSame(day, selectedDay, pickerType)}
      isHovered={isInSame(day, hoveredDay, pickerType)}
      isHighlighted={
        pickerType === 'week' ? isDateInList(highlightedDays, day) : false
      }
      weekStartPreference={weekStartPreference}
      pickerType={pickerType}
    />
  )
}

type LoadedDateCalendarProps = {
  onChange?: (newValue: Dayjs | null) => Promise<void>
  disabled?: boolean
  highlightedDays?: Dayjs[]
  initialValue?: Nullable<Dayjs>
  pickerType?: 'day' | 'week' | 'month'
  fixedWeekNumber?: number
}

const LoadedDateCalendar: FC<LoadedDateCalendarProps> = ({
  onChange,
  disabled = false,
  highlightedDays = [],
  initialValue,
  pickerType = 'day',
  fixedWeekNumber = 6,
}) => {
  const { localePreference, weekStartPreference } = useCompanySettings()
  const [value, setValue] = useState<Nullable<Dayjs>>(initialValue)

  dayjs.updateLocale(localePreference.toLocaleLowerCase(), {
    weekStart: weekStartPreference,
  })

  const [hoveredDay, setHoveredDay] = useState<Dayjs | null>(null)

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={localePreference.toLocaleLowerCase()}
    >
      <DateCalendar
        value={value}
        disabled={disabled}
        onChange={async (newValue) => {
          setValue(newValue)
          onChange && (await onChange(newValue))
        }}
        showDaysOutsideCurrentMonth
        slots={{ day: Day }}
        disableHighlightToday
        fixedWeekNumber={fixedWeekNumber}
        slotProps={{
          day: (ownerState) => ({
            selectedDay: value,
            hoveredDay,
            onPointerEnter: () => setHoveredDay(ownerState.day),
            onPointerLeave: () => setHoveredDay(null),
            weekStartPreference,
            highlightedDays,
            pickerType,
          }),
        }}
        sx={{ maxHeight: '32.4rem' }}
      />
    </LocalizationProvider>
  )
}

export default LoadedDateCalendar
